import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/PartnerAgency.graphql';
import { Query, QuerypartnerAgencyArgs } from '../../__generated__/globalTypes';

export const usePartnerAgencyQuery = (options?: QueryHookOptions<Query, QuerypartnerAgencyArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerypartnerAgencyArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const usePartnerAgencyLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerypartnerAgencyArgs>
): QueryResult<Query, QuerypartnerAgencyArgs> & {
  getPartnerAgency: LazyQueryExecFunction<Query, QuerypartnerAgencyArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getPartnerAgency, result] = useLazyQuery<Query, QuerypartnerAgencyArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getPartnerAgency, ...result };
};

export const usePartnerAgencyPromiseQuery = () => {
  const client = useApolloClient();

  const getPartnerAgency = useCallback(
    (options?: Omit<QueryOptions<QuerypartnerAgencyArgs, Query>, 'query'>) =>
      client.query<Query, QuerypartnerAgencyArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getPartnerAgency };
};
// Suspense Query Hooks
export const usePartnerAgencySuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QuerypartnerAgencyArgs>) => {
  const result = useSuspenseQuery<Query, QuerypartnerAgencyArgs>(QUERY, {
    ...options
  });

  return result;
};
